import React, { useState, useEffect } from "react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchNotificationSummaryThunk,
  readAllNotificationsThunk,
} from "../../slices/socket/notificationThunks";

//SimpleBar
import SimpleBar from "simplebar-react";

const NotificationDropdown = () => {
  //Dropdown Toggle
  const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdown(!isNotificationDropdown);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Select states from Redux
  const { summary = {}, loading = false } = useSelector(
    (state) => state.Notification || {},
  );

  // Safe extraction of counts (checking both upper and lowercase keys)
  const kycCount = summary.KYC !== undefined ? summary.KYC : summary.kyc || 0;
  const depositCount =
    summary.DEPOSIT !== undefined ? summary.DEPOSIT : summary.deposit || 0;
  const withdrawCount =
    summary.WITHDRAWAL !== undefined
      ? summary.WITHDRAWAL
      : summary.withdraw || summary.withdrawal || 0;
  const newAccountCount =
    summary.NEW_ACCOUNT !== undefined
      ? summary.NEW_ACCOUNT
      : summary.new_account || 0;
  const totalCount =
    summary.total !== undefined
      ? summary.total
      : kycCount + depositCount + withdrawCount + newAccountCount || 0;

  const hasNotifications =
    kycCount > 0 ||
    depositCount > 0 ||
    withdrawCount > 0 ||
    newAccountCount > 0;

  // Fetch summary on mount
  useEffect(() => {
    dispatch(fetchNotificationSummaryThunk());
  }, [dispatch]);

  // Handle clicking a category row
  const handleCategoryClick = async (e, type, redirectPath) => {
    if (e) e.preventDefault();
    if (loading) return; // Prevent duplicate requests

    try {
      await dispatch(readAllNotificationsThunk(type)).unwrap();
    } catch (err) {
      console.error(`Failed to mark ${type} notifications as read:`, err);
    }

    // Redirect and close dropdown
    navigate(redirectPath);
    setIsNotificationDropdown(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async (e) => {
    if (e) e.preventDefault();
    if (loading) return; // Prevent duplicate requests

    try {
      await dispatch(readAllNotificationsThunk()).unwrap();
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isNotificationDropdown}
        toggle={toggleNotificationDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-bell fs-22"></i>
          {totalCount > 0 && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {totalCount}
              <span className="visually-hidden">unread notifications</span>
            </span>
          )}
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">
                    {" "}
                    Notifications{" "}
                  </h6>
                </Col>
                <div className="col-auto dropdown-tabs">
                  <span className="badge bg-light-subtle text-body fs-13">
                    {" "}
                    {totalCount} New
                  </span>
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    disabled={loading || totalCount === 0}
                    className="btn btn-link text-white-50 p-0 text-decoration-none border-0 bg-transparent fs-12"
                    onClick={handleMarkAllAsRead}
                    style={{
                      cursor:
                        loading || totalCount === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    Mark all as read
                  </button>
                </div>
              </Row>
            </div>
          </div>

          <div className="py-2 ps-2">
            <SimpleBar className="pe-2 simplebar-height-300">
              {/* KYC Request Row */}

              {kycCount > 0 && (
                <div
                  className="text-reset notification-item d-block dropdown-item position-relative"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-16">
                        <i className="bx bx-id-card"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <Link
                        to="#"
                        className="stretched-link text-decoration-none"
                        onClick={(e) =>
                          handleCategoryClick(e, "KYC", "/account-kyc")
                        }
                        style={{ pointerEvents: loading ? "none" : "auto" }}
                      >
                        <h6 className="mt-0 mb-1 fs-14 text-dark fw-semibold">
                          KYC
                        </h6>
                      </Link>
                      <p className="mb-0 fs-12 text-muted">
                        Pending KYC approval requests
                      </p>
                    </div>
                    <div className="px-2">
                      <span
                        className={`badge ${kycCount > 0 ? "bg-danger text-white" : "bg-light text-muted"} fs-12`}
                      >
                        {kycCount}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Deposit Request Row */}

              {depositCount > 0 && (
                <div
                  className="text-reset notification-item d-block dropdown-item position-relative"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-16">
                        <i className="bx bx-wallet"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <Link
                        to="#"
                        className="stretched-link text-decoration-none"
                        onClick={(e) =>
                          handleCategoryClick(
                            e,
                            "DEPOSIT",
                            "/payments-deposit-request",
                          )
                        }
                        style={{ pointerEvents: loading ? "none" : "auto" }}
                      >
                        <h6 className="mt-0 mb-1 fs-14 text-dark fw-semibold">
                          Deposit
                        </h6>
                      </Link>
                      <p className="mb-0 fs-12 text-muted">
                        New deposit approval requests
                      </p>
                    </div>
                    <div className="px-2">
                      <span
                        className={`badge ${depositCount > 0 ? "bg-danger text-white" : "bg-light text-muted"} fs-12`}
                      >
                        {depositCount}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Withdraw Request Row */}

              {withdrawCount > 0 && (
                <div
                  className="text-reset notification-item d-block dropdown-item position-relative"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-16">
                        <i className="bx bx-money"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <Link
                        to="#"
                        className="stretched-link text-decoration-none"
                        onClick={(e) =>
                          handleCategoryClick(
                            e,
                            "WITHDRAWAL",
                            "/payments-withdraw-request",
                          )
                        }
                        style={{ pointerEvents: loading ? "none" : "auto" }}
                      >
                        <h6 className="mt-0 mb-1 fs-14 text-dark fw-semibold">
                          Withdraw
                        </h6>
                      </Link>
                      <p className="mb-0 fs-12 text-muted">
                        Withdrawal approval requests
                      </p>
                    </div>
                    <div className="px-2">
                      <span
                        className={`badge ${withdrawCount > 0 ? "bg-danger text-white" : "bg-light text-muted"} fs-12`}
                      >
                        {withdrawCount}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* New Account Row */}
              {newAccountCount > 0 && (
                <div
                  className="text-reset notification-item d-block dropdown-item position-relative"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-xs me-3">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-16">
                        <i className="bx bx-user-plus"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <Link
                        to="#"
                        className="stretched-link text-decoration-none"
                        onClick={(e) =>
                          handleCategoryClick(
                            e,
                            "NEW_ACCOUNT",
                            "/account-registered-user",
                          )
                        }
                        style={{ pointerEvents: loading ? "none" : "auto" }}
                      >
                        <h6 className="mt-0 mb-1 fs-14 text-dark fw-semibold">
                          New Account
                        </h6>
                      </Link>
                      <p className="mb-0 fs-12 text-muted">
                        New registered user accounts
                      </p>
                    </div>
                    <div className="px-2">
                      <span
                        className={`badge ${newAccountCount > 0 ? "bg-danger text-white" : "bg-light text-muted"} fs-12`}
                      >
                        {newAccountCount}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {!hasNotifications && (
                <div className="text-center py-4 text-muted">
                  <i className="bx bx-bell-off fs-1 d-block mb-2"></i>
                  No new notifications
                </div>
              )}
            </SimpleBar>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
